//@flow
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { translate } from "react-i18next";
import '../../../../css/react-draggable/css/styles.css';
import IconClose from '../../../../icons/exit.svg';
import IconBingAerial from '../../../../icons/thumbnail_bing_aerial.jpg';
import IconOpenSteetMap from '../../../../icons/thumbnail_openstreetmap.jpg';
import IconBathyMdmc from '../../../../icons/thumbnail_bathy_mdmc.jpg';
import IconBathyMdfc from '../../../../icons/thumbnail_bathy_mdfc.jpg';
import OlMap from 'ol/map';
import customControl from './customControl';
import OlSourceOSM from 'ol/source/osm';
import OlLayerTile from 'ol/layer/tile';
import OlSourceBingMaps from 'ol/source/bingmaps';
import OlLayerWMS from 'ol/layer/image';
import OlSourceImageWMS from 'ol/source/imagewms';
import basemapsIcon from '../../../../icons/basecamp.svg';

const PopupWindow = styled.div`
    background: #fff;
    border-radius: 3px;
    margin: 10px;
    float: left;
    z-index: 2;
    pointer-events: auto;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
`

const PopupHeader = styled.div`
  background-color: #265a9c;
  border-radius: 3px 3px 0 0;
  height: 24px;
  display: flex;
  justify-content: space-between;
  text-align: center;
  color: white;
  font-size: 12px;
  font-family: Open Sans, sans-serif;
  align-items: center;
  padding-left: 5px;
`

const PopupContent = styled.div`
  display: flex;
  padding: 2px;
`

const BasemapButton = styled.button`  
  background-color: rgba(86, 76, 94, 0.1);
  margin: 1px;
  text-align: center;
  font-family: Open Sans, sans-serif;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 124px;

  :hover {
    -webkit-box-shadow: 0px 0px 3px 1px rgba(0,0,0,0.3);
    box-shadow: 0px 0px 3px 1px rgba(0,0,0,0.3);
  }
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  height: 100%;   
  background-color: transparent;
  border: none;

  :hover {
    -webkit-box-shadow: 0px 0px 3px 1px rgba(0,0,0,0.14);
    box-shadow: 0px 0px 3px 1px rgba(0,0,0,0.14);
  }
  img {
    height: 18px;
  }
`;

const CloseIcon = styled.img`
`;

const StyledIcon = styled.img`
  height: 60px;
  margin: 2px;
`;

type BasemapsPopupState = {
  visible: boolean;
}

type BasemapsPopupProps = {
  olMap: OlMap
};

export class BasemapsPopup extends React.Component<BasemapsPopupProps, BasemapsPopupState> {
  bingLayer: OlLayerTile;
  osmLayer: OlLayerTile;
  basemapControl: customControl;

  constructor(props) {
    super(props);
    const { olMap, t, hasBathy }: BasemapsPopupProps = props;

    // dodanie przycisku map bazowych
    this.basemapControl = new customControl({
      onClick: this.onBasemapsButtonClick,
      className: 'ol-basemaps-button',
      title: t('Base maps'),
      innerHTML: '<img width="20px" height="20px" src=' + basemapsIcon + '></img>',
      parent: this
    });
    olMap.addControl(this.basemapControl);

    // dodanie warstw bazowych
    this.osmLayer = new OlLayerTile({
      visible: true,
      showInLegend: false,
      source: new OlSourceOSM(),
      name: "Open Street Map"
    });
    olMap.addLayer(this.osmLayer);
    this.bingLayer = new OlLayerTile({
      visible: false,
      showInLegend: false,
      name: "Orto Bing",
      source: new OlSourceBingMaps({
        key: 'Agbf-AcX6luLrI2v_Asq3TpRDAIC9aQ8m-Jd7wwX0Z8KDjpdj65KFNEoGF079LtK',
        imagerySet: "Aerial",
      })
    });
    olMap.addLayer(this.bingLayer);

    if (hasBathy) this.addBathyLayers(olMap);

    this.state = {
      visible: false
    };
  }

  addBathyLayers(olMap): void {
    this.bathyLayer = this.getBathyLayer("mean_atlas_land", "Mean depths full coverage");
    olMap.addLayer(this.bathyLayer);
    this.bathyWMTSLayer = this.getBathyLayer("mean_multicolour", "Mean depths multi colour");
    olMap.addLayer(this.bathyWMTSLayer);
  }

  getBathyLayer(layer, name) {
    return new OlLayerWMS({
      visible: false,
      showInLegend: false,
      name: name,
      extent: [-4051470, 1641318, 4794795, 15108231],
      source: new OlSourceImageWMS({
        url: 'https://ows.emodnet-bathymetry.eu/wms',
        attributions: this.getBathyAttributions(),
        params: {'LAYERS': layer}
      })
    });
  }

  getBathyAttributions() {
    const { t } = this.props;
    return t("Data displayed on this basemap was made available by the EMODnet Bathymetry project (by EMODnet Bathymetry Consortium)") +
      " <a href='https://www.emodnet-bathymetry.eu/' target='_blank'>www.emodnet-bathymetry.eu</a>, " +
      t("funded by the European Commission Directorate General for Maritime Affairs and Fisheries. The data originators can be checked") +
      " <a href='https://sextant.ifremer.fr/record/bb6a87dd-e579-4036-abe1-e649cea9881a/' target='_blank'>HERE</a>.";
  }

  onBasemapsButtonClick(mapComponent) {
    mapComponent.setState({
      visible: !mapComponent.state.visible
    });
  }

  changeBasemap(basemapName: string): void {
    const { hasBathy } = this.props;
    this.osmLayer.setVisible(basemapName === 'OSM');
    this.bingLayer.setVisible(basemapName === 'bing-aerial');
    if (hasBathy) {
      this.bathyLayer.setVisible(basemapName === 'bathy-mdfc');
      this.bathyWMTSLayer.setVisible(basemapName === 'bathy-mdmc');
    }
  }

  close(): void {
    this.setState({
      visible: false
    });
  }

  componentDidUpdate(): void {
    const { t } = this.props;
    this.basemapControl.ref.title = t('Base maps');
  }


  render(): React.Node {
    const { t, hasBathy } = this.props;
    if (this.state.visible) {
      return (
        <Draggable bounds="parent"
          defaultPosition={{ x: 40, y: 40 }}
          position={null}
          grid={[1, 1]}>
          <PopupWindow>
            <PopupHeader>
              {t('Base maps')}
              <CloseButton onClick={() => this.close()}>
                <CloseIcon src={IconClose} />
              </CloseButton>
            </PopupHeader>
            <PopupContent>
              <BasemapButton onClick={() => this.changeBasemap('OSM')}><StyledIcon src={IconOpenSteetMap} /> OSM</BasemapButton>
              <BasemapButton onClick={() => this.changeBasemap('bing-aerial')}><StyledIcon src={IconBingAerial} /> Bing</BasemapButton>
              {hasBathy && <React.Fragment>
                <BasemapButton onClick={() => this.changeBasemap('bathy-mdfc')}><StyledIcon src={IconBathyMdfc} />{t("Mean depths full coverage")}</BasemapButton>
                <BasemapButton onClick={() => this.changeBasemap('bathy-mdmc')}><StyledIcon src={IconBathyMdmc} />{t("Mean depths multi colour")}</BasemapButton>
              </React.Fragment>}
            </PopupContent>
          </PopupWindow>
        </Draggable>
      );
    } else {
      return null;
    }
  }
};

const mapStateToProps = state => ({
    hasBathy: state.domainProperties.get('hasBathyMap')
});
export default translate("translations")(connect(mapStateToProps, null)(BasemapsPopup));


// WEBPACK FOOTER //
// ./src/containers/content/map/popups/BasemapsPopup.jsx